import type { FieldLabel, PropertyPath } from "@ngt-form/core";
import { useMemo } from "react";

import type { ModelMetadata } from "src/api/Tmd.dtos";
import convertPascalToCamelCasePropertyPath from "src/utilities/convertPascalToCamelCasePropertyPath";

/**
 * Combines metadata labels with caller-provided labels, memoized by input references.
 *
 * Postconditions: Valid metadata label paths are converted to camel case; later duplicate paths
 * overwrite earlier ones and supplied labels win. Without metadata fields, returns the supplied
 * dictionary directly or an empty dictionary. Input dictionaries are not modified.
 * Assumptions: Relevant changes replace metadata/label references to invalidate the memo.
 */
const useTMDFormLabels = (
    metadata?: ModelMetadata | null | undefined,
    labels?: Record<PropertyPath, FieldLabel> | null | undefined
): Record<PropertyPath, FieldLabel> => {
    return useMemo(
        () => {
            const hasMetadata = metadata && metadata.fields && metadata.fields.length !== 0;

            if (!hasMetadata && !labels) {
                return {};
            }

            if (!hasMetadata) {
                return labels;
            }

            const output: Record<PropertyPath, FieldLabel> = {};

            for (let field of metadata.fields) {
                if (!field.label || !field.label.propertyPath || !field.label.label) {
                    continue;
                }

                const propertyPath = convertPascalToCamelCasePropertyPath(field.label.propertyPath);
                output[propertyPath] = field.label.label;
            }

            return {
                ...output,
                ...labels
            };
        },
        [metadata, labels]
    );
};

export default useTMDFormLabels;
